import { GoalTarget } from '../components/client/HealthCalendarTile';

/**
 * Evaluate a metric value against its goal and return a compliance color.
 *
 * Four bands symmetric around the goal boundary:
 * - Green:  Compliant, above the buffer threshold
 * - Yellow: Compliant, but within buffer of becoming non-compliant
 * - Orange: Non-compliant, but within buffer of becoming compliant
 * - Red:    More than buffer out of compliance
 * - null:   No goal (use default text color)
 *
 * Range/target goals: 20% of band width as buffer
 * Over/under goals: 10% of absolute target value as buffer
 */
export function getMetricComplianceColor(actual: number | null, goal: GoalTarget | undefined): string | null {
  if (!goal || actual == null || actual <= 0) return null;
  if (goal.goal_type === 'tracking_only') return null;

  const BUFFER = 0.10; // 10%
  const GREEN = '#10b981';
  const YELLOW = '#facc15';
  const ORANGE = '#fb923c';
  const RED = '#ef4444';

  switch (goal.goal_type) {
    case 'range': {
      const floor = goal.floor_value || 0;
      const ceiling = goal.goal_value;
      const bandWidth = ceiling - floor;
      const edgeBuffer = bandWidth * 0.20;

      if (actual >= floor && actual <= ceiling) {
        if (actual < floor + edgeBuffer || actual > ceiling - edgeBuffer) return YELLOW;
        return GREEN;
      } else {
        if (actual < floor) {
          return actual >= floor - edgeBuffer ? ORANGE : RED;
        } else {
          return actual <= ceiling + edgeBuffer ? ORANGE : RED;
        }
      }
    }

    case 'over': {
      const target = goal.goal_value;
      const buffer = target * BUFFER;
      if (actual >= target) {
        return actual < target + buffer ? YELLOW : GREEN;
      } else {
        return actual >= target - buffer ? ORANGE : RED;
      }
    }

    case 'under': {
      const target = goal.goal_value;
      const buffer = target * BUFFER;
      if (actual <= target) {
        return actual > target - buffer ? YELLOW : GREEN;
      } else {
        return actual <= target + buffer ? ORANGE : RED;
      }
    }

    case 'target': {
      const target = goal.goal_value;
      const variance = (goal.variance_percent || 5) / 100;
      const min = target * (1 - variance);
      const max = target * (1 + variance);
      const bandWidth = max - min;
      const edgeBuffer = bandWidth * 0.20;

      if (actual >= min && actual <= max) {
        if (actual < min + edgeBuffer || actual > max - edgeBuffer) return YELLOW;
        return GREEN;
      } else {
        if (actual < min) {
          return actual >= min - edgeBuffer ? ORANGE : RED;
        } else {
          return actual <= max + edgeBuffer ? ORANGE : RED;
        }
      }
    }

    default:
      return null;
  }
}
